<template>
  <div
    class="app-input"
    :class="stylesComputed"
  >
    <div
      v-if="hint"
      class="app-input__hint"
    >
      <small>{{ hint }}</small>
    </div>

    <!-- Error Message -->
    <div
      v-if="errorPosition === 'top'"
      class="app-input__error min-h-20 mb-1"
      :class="error ? 'opacity-100' : 'opacity-0'"
    >
      <FontAwesomeIcon
        class="app-input__error-icon mr-5"
        icon="circle-exclamation"
      />
      <slot
        v-if="$slots.error"
        name="error"
      />
      <small v-else>{{ error }}</small>
    </div>

    <div
      class="app-input__wrapper"
      :class="[wrapperStylesComputed, wrapperStylesComputedBorder, customClass]"
    >
      <!-- country-box for phone verification -->
      <span
        v-if="$slots.countryBox"
        class="app-input__country-box"
      >
        <slot name="countryBox" />
      </span>
      <!-- Label -->
      <label
        v-if="label"
        :for="name"
        class="app-input__label"
        :class="labelStylesComputed"
      >
        <span>{{ label }}</span>
      </label>

      <input
        v-if="!mask"
        :id="id"
        key="input"
        class="app-input__field"
        :class="[fieldStylesComputed, caretColorClass(400)]"
        :name="name"
        :maxlength="maxLength"
        :type="type"
        :value="value"
        :autocomplete="autocomplete"
        :pattern="pattern"
        :placeholder="placeholder"
        :enterkeyhint="enterkeyhint"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown.enter="focusNext(enterKeyEnabled, isMortgage, deviceType)"
      >

      <MaskedInput
        v-else
        :id="id"
        key="masked"
        :name="name"
        class="app-input__field"
        :class="[fieldStylesComputed, caretColorClass(400)]"
        :mask="mask"
        :guide="true"
        :placeholder="placeholder"
        :type="type"
        :pattern="pattern"
        :placeholder-char="placeholderChar"
        :value="value"
        :autocomplete="autocomplete"
        :pipe="pipe"
        :enterkeyhint="enterkeyhint"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown.enter="focusNext(enterKeyEnabled, isMortgage, deviceType)"
      />

      <!-- Icons -->
      <span
        v-if="$slots.iconLeft"
        class="app-input__icon app-input__icon--left"
      >
        <slot name="iconLeft" />
      </span>

      <span
        v-if="$slots.iconRight"
        class="app-input__icon app-input__icon--right"
      >
        <slot name="iconRight" />
      </span>
    </div>

    <!-- Dropdown wrapper slot -->
    <slot
      v-if="$slots.dropdown"
      name="dropdown"
    />

    <!-- Error Message -->
    <div
      v-if="error && errorPosition === 'bottom'"
      class="app-input__error"
    >
      <FontAwesomeIcon
        class="app-input__error-icon mr-5"
        icon="circle-exclamation"
      />
      <slot
        v-if="$slots.error"
        name="error"
      />
      <small v-else>{{ error }}</small>
    </div>
  </div>
</template>

<script>
import theme from '@/mixins/theme'
import enterKeyFunctionality from '@/mixins/enter-key-functionality'

export default {
  // Name
  name: 'AppInput',

  components: {
    MaskedInput: () => import(/* webpackChunkName: "masked-input" */ 'vue-text-mask')
  },

  // Mixins
  mixins: [theme, enterKeyFunctionality],

  // Props
  props: {
    autocomplete: {
      type: String,
      default: undefined
    },
    iconLeft: {
      type: Boolean,
      default: false
    },
    iconRight: {
      type: Boolean,
      default: false
    },
    id: {
      type: String,
      default: undefined
    },
    error: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    hint: {
      type: String,
      default: ''
    },
    maxWidth: {
      type: String,
      default: 'max-w-500'
    },
    name: {
      type: String,
      default: '',
      required: true
    },
    type: {
      type: String,
      default: 'text'
    },
    value: {
      type: [Number, String],
      default: ''
    },
    maxLength: {
      type: Number,
      default: null
    },
    verificationCode: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: undefined
    },
    placeholderChar: {
      type: String,
      default: ''
    },
    pattern: {
      type: [RegExp, String],
      default: undefined
    },
    mask: {
      type: [Array, String],
      default: ''
    },
    split: {
      type: Boolean,
      default: false
    },
    pipe: {
      type: Function,
      default: (conformedValue) => {
        return conformedValue
      }
    },
    customClass: {
      type: String,
      default: ''
    },
    errorPosition: {
      type: String,
      default: 'bottom' // 'top' or 'bottom'
    },
    enterKeyEnabled: {
      type: Boolean,
      default: true
    },
    enterkeyhint: {
      type: String,
      default: 'next'
    }
  },

  // Data
  data () {
    return {
      focused: false,
      deviceType: ''
    }
  },

  // Computed
  computed: {
    stylesComputed () {
      return {
        [this.maxWidth]: this.maxWidth,
        'error': this.error,
        'flex lg:max-w-500 mt-40 mb-25 sm:my-30': this.$slots.countryBox,
        'w-full mb-15 sm:mb-30 lg:max-w-400 sm:max-w-500': !this.verificationCode,
        'app-input--verification-code': this.verificationCode
      }
    },
    labelStylesComputed () {
      return {
        'app-input__label--floating': !this.split && (this.focused || this.value),
        'app-input__label--icon-left': this.iconLeft,
        'absolute': !this.split,
        'text-gray-500 order-2 w-1/2 border-l border-purple-100 my-10 flex justify-center items-center': this.split
      }
    },
    fieldStylesComputed () {
      return {
        'app-input__field--mask': this.mask,
        'app-input__field--icon-left': this.iconLeft,
        'app-input__field--icon-right': this.iconRight,
        'w-full': !this.split,
        'app-input__field--split': this.split,
        'tracking-widest': this.$slots.countryBox || this.verificationCode,
        'app-input__field--verification-code': this.verificationCode,
        'app-input__field--no-label': !this.label
      }
    },
    wrapperStylesComputed () {
      return {
        'app-input__wrapper--error': this.error,
        'app-input__wrapper--floating': (this.focused || this.value) && this.label,
        'app-input__wrapper--country-box': this.$slots.countryBox,
        'app-input__wrapper--split': this.split
      }
    },
    wrapperStylesComputedBorder () {
      if (this.focused) return this.borderColorClass(400)
      return 'border-purple-100'
    },
    errorStylesComputed () {
      return {
        'app-input__error--country-box': this.$slots.countryBox
      }
    },
    isMobile () {
      return this.$store.state.tracking.device_type === 'm'
    },
    isMortgage () {
      return this.$store.getters['loans/isMortgage']
    }
  },

  mounted () {
    this.deviceType = this.isMobileOrTablet()
  },

  unmounted () {
    document.removeEventListener('touchmove', this.handleTouchMove)
  },

  // Methods
  methods: {
    /**
     * Handle Input
     * @emits input - the value of the input
     * @emits filled - if masked, the value does not contain the placeholder character
     */
    handleInput (event) {
      if (this.mask) {
        this.$emit('input', event)

        if (event && !event.includes(this.placeholderChar)) {
          this.$emit('filled')
        }
      } else {
        this.$emit('input', event.target.value)
      }
    },

    /**
     * Handle Focus
     * sets 'focused' to true
     * @emits focus
     */
    handleFocus () {
      this.$store.dispatch('setInputFocused', true)
      this.$emit('focus')
      this.focused = true
      // attach scroll handler here for mobile
      if (this.isMobile) {
        document.addEventListener('touchmove', (event) => {
          this.handleTouchMove()
        }, { once: true })
      }
    },

    /**
     * Handle Blur
     * sets 'focused' to false
     * @emits blur
     */
    handleBlur (e) {
      this.$store.dispatch('setInputFocused', false)
      this.$emit('blur', e)
      this.focused = false
    },

    handleTouchMove () {
      document.activeElement.blur()
    },

    isMobileOrTablet () {
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 1
      const isSmallScreen = Math.min(window.innerWidth, window.innerHeight) <= 1024

      return hasTouch && isSmallScreen ? 'm' : 'd'
    }
  }
}
</script>
<style scoped lang="scss">
.app-input {
  @apply block m-auto mb-15 relative w-full;

  @screen sm {
    @apply mb-30;
  }

  &--verification-code {
    @apply mb-15 max-w-170;
  }

  &__hint {
    @apply text-gray-400 pointer-events-none select-none;
  }

  &__wrapper {
    @apply block h-55 outline-none rounded-full w-full;
    @apply border-2 flex items-center relative;

    @screen sm {
      @apply h-60;
    }
  }

  &__wrapper--country-box {
    .app-input__label {
      @apply left-[110px];
    }

    .app-input__field {
      @apply pl-110;
    }
  }

  &__wrapper--error {
    @apply border-red-400;
  }

  &__wrapper--split {
    @apply bg-white;
  }

  &__label {
    @apply left-[25px] text-gray-500 pointer-events-none truncate;

    transform-origin: 0 0;
    transform: translate3d(0, 0, 0);
    transition: transform 175ms ease-out;
  }

  &__label--floating {
    transform: translate3d(0, -12.5px, 0) scale(0.75);
  }

  &__label--icon-left {
    @apply left-[30px];
  }

  &__country-box {
    @apply absolute h-full;
  }

  :deep() .country-box {
    @apply relative flex justify-center items-center flex-shrink-0;
    @apply h-full px-25 rounded-l-full text-gray-500 border-r-1;

    img {
      @apply h-auto mr-5 max-w-20;
    }
  }

  &__field {
    @apply bg-white h-full outline-none px-25 pt-25 pb-10 rounded-full w-full;

    &::placeholder {
      @apply text-black-500;
    }

    &:focus::placeholder {
      @apply text-transparent;
    }

    &:-webkit-autofill {
      -webkit-box-shadow: 0 0 0 1000px #fff inset !important;
    }

    &--icon-right {
      @apply pr-45;
    }
  }

  &__field--mask {
    &::placeholder {
      @apply text-transparent;
    }

    &:focus::placeholder {
      @apply text-gray-400;
    }
  }

  &__field--verification-code {
    @apply text-center p-0 text-lg;

    @screen sm {
      @apply text-xl;
    }
  }

  &__field--split {
    @apply pb-25 w-1/2 order-1 text-center rounded-r-none;
  }

  &__field--icon-left {
    @apply pl-30;
  }

  &__field--no-label {
    @apply py-0;
  }

  &__icon {
    @apply absolute;

    &--right {
      @apply right-[15px];
    }

    &--left {
      @apply left-[15px];
    }
  }

  &__error {
    @apply text-red-400;
  }
}
</style>
